import { getErrorMessageKey } from './i18n/errors.ts';

// The tab's recent unexpected errors, which a reported problem sends along (plan task 98). Kept in memory only, so a
// reload starts it empty.
const MAX_ENTRIES = 10;
const MAX_ANSWER_LENGTH = 500;
const MAX_STACK_LINES = 10;

let entries: string[] = [];
// The same error reaches several places (a mutation's cache and the form that ran it), but is logged once.
const recorded = new WeakSet<object>();

const describe = (error: unknown): string => {
    if (!(error instanceof Error)) return String(error);
    // The API's errors (api/client.ts), read by their fields so the core doesn't depend on the API.
    if ('request' in error && 'status' in error) {
        return `${String(error.request)} → ${String(error.status)}\n${error.message.slice(0, MAX_ANSWER_LENGTH)}`;
    }
    const stack = error.stack?.split('\n').slice(0, MAX_STACK_LINES).join('\n');
    return stack?.includes(error.message) ? stack : `${error.name}: ${error.message}\n${stack ?? ''}`.trim();
};

// Only what gets the generic message (getErrorMessageKey) is unexpected; an error with a code was planned for and
// says what to do.
export const recordError = (error: unknown) => {
    // A script or image that didn't load fires `error` without one.
    if (error === null || error === undefined || getErrorMessageKey(error) !== 'errors.failed') return;
    if (typeof error === 'object' && error !== null) {
        if (recorded.has(error)) return;
        recorded.add(error);
    }
    const entry = `${new Date().toISOString()} · ${window.location.pathname}\n${describe(error)}`;
    entries = [entry, ...entries].slice(0, MAX_ENTRIES);
};

// Newest first, as one text cut from the oldest entries to fit `maxLength`.
export const readErrorLog = (maxLength: number): { text: string; count: number } => {
    const kept: string[] = [];
    for (const entry of entries) {
        if ([...kept, entry].join('\n\n').length > maxLength) break;
        kept.push(entry);
    }
    return { text: kept.join('\n\n'), count: kept.length };
};

export const clearErrorLog = () => {
    entries = [];
};
